import { AddCircleOutline, Delete } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdEdit } from 'react-icons/md'
import { toast } from 'react-toastify'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import {
  invalidateTaskResourceCache,
  resourceEndpoints,
  useAddResource,
  useDeleteResource,
  useUpdateResource,
} from '../../../../api/task-resource'
import GenericTable from '../../../../components/table/GenericTable'
import { CANNOT_ADD, SUCCESSFULLY_ADDED } from '../../../../constant'
import { useCustomerCtx, useDialogCtx } from '../../../../hooks/context-hooks'
import { EdgeCase, getUserDetail } from '../../../../utils/getUserDetails'
import RelatedTasksDialog from '../tasks/RelatedTasksDialog'
import ResourceFrom from './ResourceFrom'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'

const Resources = () => {
  const { customer } = useCustomerCtx()
  const theme = useTheme()
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const [selectedId, setSelectedId] = useState(0)
  const { mutateAsync, isLoading: isLoadingAdd } = useAddResource(customer?.id)
  const { mutateAsync: updateMutate, isLoading: isLoadingUpdate } = useUpdateResource(customer?.id, selectedId)
  const { mutateAsync: deleteMutate } = useDeleteResource(customer?.id)
  const { t: translate } = useTranslation(['task', 'common'])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const showTasks = useCallback(
    row => {
      showDialog(
        RelatedTasksDialog,
        {
          componentProps: {
            customerId: customer.id,
            taskId: row.id,
          },
          opts: {
            maxWidth: 'md',
            fullWidth: true,
          },
        },
        undefined,
        true,
      )
    },
    [customer.id, showDialog, translate],
  )

  const onAdd = useCallback(() => {
    showDialog(ResourceFrom, {
      title: translate('add-resource'),
      opts: {
        maxWidth: 'sm',
        fullWidth: true,
      },
      componentProps: {
        onSubmit: async (values, popCallback) => {
          await mutateAsync(values, {
            onSuccess: data => {
              if (data.data === SUCCESSFULLY_ADDED) {
                toast.success(translate('resourceAddedSuccessfully', { ns: 'task' }))
                invalidateTaskResourceCache.getAllResources(customer?.id)
              }
              popCallback()
            },
            onError: error => {
              if (error['data'] === CANNOT_ADD) {
                toast.error(translate('cannot-add-same-resource-name'))
              } else {
                toast.error(translate('addingError', { ns: 'common' }))
              }
            },
          })
        },
        customerId: customer.id,
        initialValues: {},
        isLoading: isLoadingAdd,
      },
    })
  }, [customer, translate, isSubmitting, mutateAsync, showDialog])

  const onUpdate = useCallback(
    row => {
      setSelectedId(row.id)
      showDialog(ResourceFrom, {
        title: translate('edit-resource'),
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        componentProps: {
          onSubmit: async (values, popCallback) => {
            setIsSubmitting(true)
            try {
              await updateMutate(values, {
                onSuccess: () => {
                  toast.success(translate('resourceUpdatedSuccessfully', { ns: 'task' }))
                  invalidateTaskResourceCache.getAllResources(customer?.id)
                  invalidateTaskResourceCache.getAllUnAssignedResources(customer?.id)
                  setIsSubmitting(false)
                  popCallback()
                },
                onError: () => {
                  toast.error(translate('updatingError', { ns: 'common' }))
                  setIsSubmitting(false)
                },
              })
            } catch (error) {
              setIsSubmitting(false)
            }
          },
          customerId: customer.id,
          initialValues: { ...row },
          isUpdateForm: true,
          isLoading: isSubmitting,
        },
      })
    },
    [customer, translate, isSubmitting, updateMutate, showDialog],
  )

  const onDelete = useCallback(
    row => {
      setSelectedId(row.id)
      showConfirmDialog(
        translate('delete-resource-title'),
        translate('delete-resource-content'),
        {
          acceptText: translate('yes', { ns: 'common' }),
          cancelText: translate('no', { ns: 'common' }),
        },
        async pop => {
          await deleteMutate(row.id, {
            onSuccess: () => {
              toast.success(translate('resourceDeletedSuccessfully', { ns: 'task' }))
              invalidateTaskResourceCache.getAllResources(customer?.id)
              invalidateTaskResourceCache.getAllUnAssignedResources(customer?.id)
              pop()
            },
            onError: () => {
              toast.error(translate('deletingError', { ns: 'common' }))
            },
          })
        },
      )
    },
    [customer, translate],
  )

  const STATIC_COLUMNS: GridColumns = useMemo(
    () => [
      {
        field: 'name',
        flex: 1,
        headerName: translate('name', { ns: 'common' }),
        cellClassName: 'font-weight-semi-bold',
        renderCell: row => (
          <DynamicTextTypography text={row.row.name} maxLength={60} variant="body1" fontWeight={500} />
        ),
      },
      {
        field: 'description',
        flex: 1,
        headerName: translate('description', { ns: 'common' }),
        renderCell: row => (
          <DynamicTextTypography text={row.row.description} maxLength={60} variant="body1" fontWeight={500} />
        ),
      },
      {
        field: 'user',
        flex: 1,
        headerName: translate('responsibleUser', { ns: 'common' }),
        renderCell: row =>
          row?.row?.user == null ? '' : getUserDetail(row?.row?.user, undefined, EdgeCase.ReturnEmailForNewUser),
      },
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        width: 10,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key={'edit'}
            label={translate('edit', { ns: 'common' })}
            icon={<MdEdit size={24} />}
            onClick={() => onUpdate(row)}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={'delete'}
            label={translate('delete', { ns: 'common' })}
            icon={<Delete fontSize={'medium'} />}
            onClick={() => onDelete(row)}
            color="primary"
            showInMenu
          />,
        ],
      },
    ],
    [translate],
  )

  return (
    <CommonPageLayout
      titleSection={{ title: translate('resources') }}
      topAction={[
        {
          buttonText: translate('resource'),
          buttonIcon: <AddCircleOutline />,
          onClick: onAdd,
          variant: 'outlined',
          shadowWeight: null,
          border: 0,
        },
      ]}
    >
      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        <GenericTable
          queryUrl={resourceEndpoints.baseURL(customer?.id)}
          columns={STATIC_COLUMNS}
          queryParams={{ isIgnoreQueryFilters: true }}
          onRowClick={({ row }) => showTasks(row)}
          enableServerPagination
          id={'customer-resources'}
          autoHeight={false}
          height={window.innerHeight - 230 + 'px'}
        />
      </Box>
    </CommonPageLayout>
  )
}

export default Resources
